import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Component, Inject, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { ApiService } from '../../services/api.service';
import { AuthService } from '../../core/services/auth.service';

@Component({
  selector: 'app-employee-documents',
  standalone: true,
  imports: [
    CommonModule, FormsModule, MatDialogModule, MatButtonModule, MatIconModule,
    MatFormFieldModule, MatInputModule, MatSelectModule, MatProgressSpinnerModule,
    MatTabsModule, MatTooltipModule, MatDividerModule, MatChipsModule
  ],
  templateUrl: './employee-documents.component.html',
  styleUrl: './employee-documents.component.scss'
})
export class EmployeeDocumentsComponent implements OnInit, OnDestroy {
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly center = signal<any | null>(null);
  readonly versions = signal<any[]>([]);
  readonly showVersionsFor = signal<string | null>(null);
  readonly previewUrl = signal<SafeResourceUrl | null>(null);
  readonly previewRawUrl = signal<string | null>(null);
  readonly previewName = signal<string | null>(null);
  readonly previewIsPdf = signal(false);
  readonly uploading = signal(false);

  canWrite = false;
  canVerify = false;

  uploadTypeId = '';
  uploadCustomTitle = '';
  uploadExpiry = '';
  uploadRemarks = '';
  selectedFile: File | null = null;

  constructor(
    private api: ApiService,
    private auth: AuthService,
    private sanitizer: DomSanitizer,
    public dialogRef: MatDialogRef<EmployeeDocumentsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { employeeId: string; employeeName?: string }
  ) {
    // Backend enforces RBAC; UI mirrors role grants for documents.read/write/verify.
    this.canWrite = this.auth.hasAnyRole(['hr', 'super_admin', 'tenant_admin', 'employee']);
    this.canVerify = this.auth.hasAnyRole(['hr', 'super_admin', 'tenant_admin']);
  }

  ngOnInit(): void {
    this.reload();
  }

  ngOnDestroy(): void {
    this.clearPreview();
  }

  reload(): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.getEmployeeDocumentCenter(this.data.employeeId).subscribe({
      next: (res) => {
        this.center.set(res?.data ?? res);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.error?.message || err?.message || 'Failed to load Document Center.');
        this.loading.set(false);
      }
    });
  }

  onFilePicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    this.selectedFile = file;
    input.value = '';
  }

  upload(): void {
    if (!this.selectedFile || !this.uploadTypeId) {
      this.error.set('Select a document type and file.');
      return;
    }
    const selectedType = this.typeOptions().find((t) => t.id === this.uploadTypeId);
    if (selectedType?.requiresExpiry && !this.uploadExpiry) {
      this.error.set(`Expiry date is required for ${selectedType.label}.`);
      return;
    }
    const center = this.center();
    const max = center?.maxFileSizeBytes ?? 10 * 1024 * 1024;
    if (this.selectedFile.size > max) {
      this.error.set(`File must be ${(max / (1024 * 1024)).toFixed(0)} MB or smaller.`);
      return;
    }

    this.uploading.set(true);
    this.error.set(null);
    this.api.uploadEmployeeDocument(this.data.employeeId, this.selectedFile, {
      documentTypeId: this.uploadTypeId,
      customTitle: this.uploadCustomTitle || undefined,
      expiryDate: this.uploadExpiry || undefined,
      remarks: this.uploadRemarks || undefined
    }).subscribe({
      next: () => {
        this.uploading.set(false);
        this.selectedFile = null;
        this.uploadCustomTitle = '';
        this.uploadExpiry = '';
        this.uploadRemarks = '';
        this.reload();
      },
      error: (err) => {
        this.uploading.set(false);
        this.error.set(err?.error?.message || err?.message || 'Upload failed.');
      }
    });
  }

  replace(doc: any, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    this.uploading.set(true);
    this.api.replaceEmployeeDocument(this.data.employeeId, doc.id, file).subscribe({
      next: () => { this.uploading.set(false); this.reload(); },
      error: (err) => {
        this.uploading.set(false);
        this.error.set(err?.error?.message || 'Replace failed.');
      }
    });
  }

  download(doc: any): void {
    this.api.downloadEmployeeDocument(this.data.employeeId, doc.id).subscribe({
      next: (blob) => this.saveBlob(blob, doc.fileName || 'document'),
      error: () => this.error.set('Download failed.')
    });
  }

  preview(doc: any): void {
    if (!doc.canPreview) {
      this.download(doc);
      return;
    }
    this.clearPreview();
    this.api.previewEmployeeDocument(this.data.employeeId, doc.id).subscribe({
      next: (blob) => {
        const raw = URL.createObjectURL(blob);
        this.previewRawUrl.set(raw);
        this.previewUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(raw));
        this.previewName.set(doc.fileName || doc.displayName);
        this.previewIsPdf.set(
          (blob.type || '').includes('pdf') ||
          String(doc.fileName || '').toLowerCase().endsWith('.pdf') ||
          String(doc.contentType || '').includes('pdf')
        );
      },
      error: () => this.error.set('Preview failed.')
    });
  }

  deleteDoc(doc: any): void {
    if (!confirm(`Delete "${doc.displayName}" and all versions?`)) return;
    this.api.deleteEmployeeDocument(this.data.employeeId, doc.id).subscribe({
      next: () => this.reload(),
      error: (err) => this.error.set(err?.error?.message || 'Delete failed.')
    });
  }

  verify(doc: any, isVerified: boolean, reject = false): void {
    const remarks = reject
      ? (prompt('Rejection remarks (optional):') ?? undefined)
      : undefined;
    this.api.verifyEmployeeDocument(this.data.employeeId, doc.id, isVerified, remarks || undefined, reject).subscribe({
      next: () => this.reload(),
      error: (err) => this.error.set(err?.error?.message || 'Verification update failed.')
    });
  }

  selectedTypeRequiresExpiry(): boolean {
    return !!this.typeOptions().find((t) => t.id === this.uploadTypeId)?.requiresExpiry;
  }

  openVersions(doc: any): void {
    this.showVersionsFor.set(doc.id);
    this.api.getEmployeeDocumentVersions(this.data.employeeId, doc.id).subscribe({
      next: (res) => this.versions.set(res?.data ?? res ?? []),
      error: () => this.versions.set([])
    });
  }

  downloadVersion(docId: string, version: any): void {
    this.api.downloadEmployeeDocumentVersion(this.data.employeeId, docId, version.id).subscribe({
      next: (blob) => this.saveBlob(blob, version.originalFileName || `v${version.versionNumber}`),
      error: () => this.error.set('Version download failed.')
    });
  }

  clearPreview(): void {
    const url = this.previewRawUrl();
    if (url) URL.revokeObjectURL(url);
    this.previewRawUrl.set(null);
    this.previewUrl.set(null);
    this.previewName.set(null);
    this.previewIsPdf.set(false);
  }

  close(): void {
    this.dialogRef.close();
  }

  formatSize(bytes?: number | null): string {
    if (!bytes && bytes !== 0) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  typeOptions(): any[] {
    const c = this.center();
    if (!c?.categories) return [];
    return c.categories.flatMap((cat: any) =>
      (cat.slots || []).map((s: any) => ({
        id: s.documentTypeId,
        label: `${cat.categoryLabel} · ${s.typeName}`,
        allowsMultiple: s.allowsMultiple,
        requiresExpiry: s.requiresExpiry
      }))
    );
  }

  private saveBlob(blob: Blob, name: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  }
}
